import {
  Box,
  Button,
  Container,
  Flex,
  Heading,
  Image,
  Text,
  Link,
  VStack,
} from '@ritualco/jutsu'
import * as Sentry from '@sentry/react'
import { useContent } from 'lib/content/react'
import { FormikInput } from 'components/Formik'
import WelcomeImage from 'assets/Images/Welcome.png'
import DownloadAppStore from 'assets/Images/DownloadAppStore.png'
import DownloadGooglePlay from 'assets/Images/DownloadGooglePlay.png'
import { LoadingScreen } from 'components/Loading'
import useBranchSms from 'hooks/useBranchSms'
import MobileDetect from 'mobile-detect'
import {
  ConnectedApplication,
  ConnectedApplicationPlatform,
  ConnectedApplicationType,
} from 'lib/generated/api'
import { Form, Formik } from 'formik'
import { useAnalytics } from 'lib/simple-analytics'

export type MemberAcceptedProps = {
  isLoading: boolean
  name: string
  organizationName: string
  workspaceName: string
  actions: ConnectedApplication[]
}

const MemberAccepted = ({
  name,
  organizationName,
  workspaceName,
  actions,
  isLoading,
}: MemberAcceptedProps) => {
  const { formatMessage } = useContent()
  const { sendSMS, status, errorMessage } = useBranchSms()
  const { track } = useAnalytics()

  const md = new MobileDetect(window.navigator.userAgent)
  const getUserPlatform = () => {
    switch (md.os()) {
      case 'iOS':
        return ConnectedApplicationPlatform.Ios
      case 'Android':
        return ConnectedApplicationPlatform.Android
      default:
        return ConnectedApplicationPlatform.Web
    }
  }

  const sortedActions = actions.sort((a, b) => {
    let priority = [
      ConnectedApplicationType.Ritual,
      ConnectedApplicationType.PropertyManager,
    ]

    return priority.indexOf(a.type) - priority.indexOf(b.type)
  })

  const renderDownloadButton = (application: ConnectedApplication) => {
    if (
      application.platform === ConnectedApplicationPlatform.Android &&
      application.type === ConnectedApplicationType.Ritual
    ) {
      return (
        <Link
          href={application.url}
          isExternal
          onClick={() =>
            track('PORTAL_MEMBERACCEPTED_APPLINK', {
              type: application.type,
              platform: application.platform,
            })
          }
        >
          <Image src={DownloadGooglePlay} width="auto" maxH="50px" />
        </Link>
      )
    } else if (
      application.platform === ConnectedApplicationPlatform.Ios &&
      application.type === ConnectedApplicationType.Ritual
    ) {
      return (
        <Link
          href={application.url}
          isExternal
          onClick={() =>
            track('PORTAL_MEMBERACCEPTED_APPLINK', {
              type: application.type,
              platform: application.platform,
            })
          }
        >
          <Image src={DownloadAppStore} width="auto" maxH="50px" />
        </Link>
      )
    }

    return null
  }

  if (isLoading) return <LoadingScreen />

  return (
    <Container maxW="540px" my={20} p={0} centerContent>
      <Box w="100%" p={4} pt={18}>
        <VStack textColor="grayscale.primary" spacing={12}>
          <Image src={WelcomeImage} width="auto" height="auto" />
          <VStack spacing={4} textAlign="center">
            <Heading fontSize="xl" whiteSpace="break-spaces">
              {formatMessage(
                { id: 'pmportal.memberAccepted.header' },
                {
                  name,
                  organizationName,
                  workspaceName,
                }
              )}
            </Heading>
            <Text fontSize="md">
              {formatMessage({
                id: 'pmportal.memberAccepted.downloadRitual',
              })}
            </Text>
          </VStack>
          <VStack width="100%" mt={12} display={{ md: 'none' }}>
            {sortedActions.map(action => {
              if (action.platform === getUserPlatform()) {
                return (
                  <Button
                    as="a"
                    onClick={() =>
                      track('PORTAL_MEMBERACCEPTED_APPLINK', {
                        type: action.type,
                        platform: action.platform,
                      })
                    }
                    href={action.url}
                    width="100%"
                    variant={
                      action.type === ConnectedApplicationType.Ritual
                        ? 'solid'
                        : 'outline'
                    }
                    colorScheme={
                      action.type === ConnectedApplicationType.Ritual
                        ? 'teal'
                        : 'gray'
                    }
                  >
                    {formatMessage(
                      { id: 'pmportal.memberAccepted.mobileDownloadButton' },
                      { appName: action.name }
                    )}
                  </Button>
                )
              }

              return null
            })}
          </VStack>

          <Flex
            justifyContent="space-around"
            width="70%"
            mx="auto"
            display={{ base: 'none', md: 'flex' }}
          >
            {actions.map(application => renderDownloadButton(application))}
          </Flex>
          <VStack width="100%" display={{ base: 'none', md: 'flex' }}>
            <Formik
              initialValues={{ phone: '' }}
              onSubmit={values => {
                sendSMS(
                  values.phone,
                  {
                    feature: 'TextMeTheApp',
                    campaign: 'WebSignup',
                    data: {
                      $custom_sms_text: formatMessage({
                        id: 'pmportal.memberAccepted.branchSms',
                      }),
                      $og_title: formatMessage({
                        id: 'pmportal.memberAccepted.branchTitle',
                      }),
                      $og_description: formatMessage({
                        id: 'pmportal.memberAccepted.branchDescription',
                      }),
                      $og_image_url: formatMessage({
                        id: 'pmportal.memberAccepted.branchImage',
                      }),
                      promo_code: formatMessage({
                        id: 'pmportal.memberAccepted.branchPromo',
                      }),
                      s: 8,
                    },
                  },
                  {
                    onError: (error: string) => {
                      track('PORTAL_MEMBERACCEPTED_BRANCH_ERROR', {
                        error: error,
                      })
                      Sentry.captureException(`Branch Error: ${error}`)
                    },
                    onSuccess: () => {
                      track('PORTAL_MEMBERACCEPTED_BRANCH_SUCCESS')
                    },
                  }
                )
              }}
            >
              <Flex as={Form} width="100%" justifyContent="space-between">
                <VStack
                  maxW="355px"
                  width="100%"
                  alignItems="flex-start"
                  paddingRight={3}
                >
                  <FormikInput
                    label={formatMessage({
                      id: 'pmportal.memberAccepted.smsLabel',
                    })}
                    name="phone"
                    placeholder="123 456 7890"
                  />
                </VStack>

                <Button
                  alignSelf="flex-end"
                  paddingX={15}
                  type="submit"
                  disabled={status === 'loading'}
                >
                  {formatMessage({
                    id: 'pmportal.memberAccepted.smsSend',
                  })}
                </Button>
              </Flex>
            </Formik>

            {status === 'success' ? (
              <Text color="primary.normal" alignSelf="flex-start" fontSize="xs">
                {formatMessage({
                  id: 'pmportal.memberAccepted.smsSuccess',
                })}
              </Text>
            ) : (
              status === 'error' && (
                <Text alignSelf="flex-start" fontSize="xs" color="error.normal">
                  {formatMessage({
                    id: 'pmportal.memberAccepted.smsError',
                  })}
                </Text>
              )
            )}
          </VStack>
        </VStack>
      </Box>
    </Container>
  )
}

export default MemberAccepted
